import { TFileLocal } from '@models/files';
import { Locale, NodeEnv, AppMode } from '@constants/common';
import { pages } from '@constants/pages';

export const getUniqueId = (idLength = 7): string => {
  let text = '';
  const possible =
    'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';

  for (let i = 0; i < idLength; i++) {
    text += possible.charAt(Math.floor(Math.random() * possible.length));
  }

  return text;
};

export const extend = <ObjA, ObjB>(a: ObjA, b: ObjB): ObjA & ObjB => ({
  ...a,
  ...b,
});

const DATE_PART_LENGTH = 2;
const DATE_PART_PAD = '0';

const padDatePart = (value: number | string): string =>
  `${value}`.padStart(DATE_PART_LENGTH, DATE_PART_PAD);

/** An instant that already states its zone: a trailing `Z`, or a `+hh:mm` / `-hhmm` offset. */
const ZONED = /(Z|[+-]\d{2}:?\d{2})$/;
const UTC_SUFFIX = /\s*UTC\s*$/i;
/** "2026-08-31 11:26:00", the shape AI history sends once its " UTC" marker is stripped. */
const BARE_TIMESTAMP =
  /^(\d{4}-\d{2}-\d{2})[ T](\d{2}:\d{2}(?::\d{2})?(?:\.\d+)?)$/;
/** "10/21/2026 02:15:51 PM": month/day/year with a 12-hour clock. */
const US_TIMESTAMP =
  /^(\d{1,2})\/(\d{1,2})\/(\d{4}) (\d{1,2}):(\d{2})(?::(\d{2}))? ?(AM|PM)$/i;
const HOURS_IN_HALF_DAY = 12;

const usTimestampToIso = (value: string): string => {
  const match = US_TIMESTAMP.exec(value);

  if (!match) return '';

  const [, month, day, year, hours, minutes, seconds = '00', meridiem] = match;
  const isPm = meridiem.toUpperCase() === 'PM';
  // 12 AM is midnight, 12 PM is noon.
  const hours24 =
    (Number(hours) % HOURS_IN_HALF_DAY) + (isPm ? HOURS_IN_HALF_DAY : 0);

  return `${year}-${padDatePart(month)}-${padDatePart(day)}T${padDatePart(
    hours24
  )}:${minutes}:${seconds}Z`;
};

/**
 * Rewrites an AI history date as strict ISO in UTC: "2026-08-31 11:26:00 UTC" and
 * "10/21/2026 02:15:51 PM UTC" both become "…T…Z".
 *
 * The value has to survive every engine's parser: the trailing " UTC" is not standard and
 * some (Safari) reject it, as they do the US form, while simply dropping the marker would
 * make `Date` read the timestamp as the viewer's local clock — hours off the instant it
 * means. Anything already carrying a zone is left as is, anything unrecognizable becomes
 * an empty string the caller reads as "no date".
 */
export const toIsoUtc = (value: string): string => {
  const trimmed = value.trim();

  if (ZONED.test(trimmed)) {
    return trimmed;
  }

  const withoutMarker = trimmed.replace(UTC_SUFFIX, '').trim();
  const bare = BARE_TIMESTAMP.exec(withoutMarker);

  return bare ? `${bare[1]}T${bare[2]}Z` : usTimestampToIso(withoutMarker);
};

/** Timestamp of an AI history date (see `toIsoUtc`); `NaN` when unparseable. */
export const parseHistoryDate = (date: string): number =>
  Date.parse(toIsoUtc(date));

/** Newest first by AI history date; an unreadable date sorts as the oldest. */
export const sortByDate = <Obj>(array: ({ date: string } & Obj)[]) =>
  [...array].sort(
    (a, b) => (parseHistoryDate(b.date) || 0) - (parseHistoryDate(a.date) || 0)
  );

const DEFAULT_PERCENT_FRACTION_DIGITS = 2;

/** A number or numeric string as a number; anything unparsable becomes 0. */
export const toNumber = (value: number | string | undefined): number => {
  const parsedValue = Number(value);

  return Number.isFinite(parsedValue) ? parsedValue : 0;
};

/** A percentage rounded to `fractionDigits` decimals, kept a number. */
export const toRoundedPercent = (
  value: number,
  fractionDigits = DEFAULT_PERCENT_FRACTION_DIGITS
): number => Number(value.toFixed(fractionDigits));

export const formateDate = (value: string | number): string => {
  const date = new Date(value);

  if (date) {
    const day = padDatePart(date.getDate());
    const month = padDatePart(date.getMonth() + 1);

    return `${day}/${month}/${date.getFullYear()}`;
  }
  return '';
};

export const hasTrailingSlash = (string: string): boolean =>
  string.endsWith('/');

export const removeBeginningSlash = (string: string) => {
  const SLASH = '/';

  if (string.startsWith(SLASH)) {
    return string.slice(1);
  }

  return string;
};

export const capitalizeFirstLetter = (value: string): string =>
  value ? `${value.charAt(0).toUpperCase()}${value.slice(1)}` : value;

export const isEnvDevelopment = () =>
  (process.env.REACT_APP_ENV || process.env.NODE_ENV) === NodeEnv.DEVELOPMENT;

export const isEnvProduction = () =>
  (process.env.REACT_APP_ENV || process.env.NODE_ENV) === NodeEnv.PRODUCTION;

export const isStartMode = (): boolean =>
  process.env.REACT_APP_MODE === AppMode.START;

export const getSourceUrl = () => {
  // A non-http referrer (app hand-off, `about:`-family) makes `new URL` throw.
  // This runs while building request payloads inside async flows, where the
  // throw would surface as an unowned rejection.
  try {
    if (document.referrer) {
      return new URL(document.referrer).pathname;
    }
  } catch {
    return window.location.pathname;
  }

  return window.location.pathname;
};

export const getRandomItem = <T>(array: T[]): T =>
  array[Math.floor(Math.random() * array.length)];

export const getRandomNumber = (min: number, max: number): number =>
  Math.floor(Math.random() * (max - min + 1)) + min;

export const getAdditionalFilesIds = (files: TFileLocal[]) =>
  files.map((file) => file.additionalId);

export const isGetSolvedAi = () =>
  window.siteVars?.REACT_APP_SITE_NAME === 'aaab29dd8bbe1a9a1f3752ba6d3e9052';

export const isPassCheckAi = () =>
  window.siteVars?.REACT_APP_SITE_NAME === '341a62d3e66f64aec95d0ca50bdc84ca';

export const reloadPage = () => {
  window.location.reload();
};

/**
 * Where the host itself sends a guest from a cabinet page (`/needlogin` ->
 * `/?login-first=1`): the home page opens its login popup. Not `/logout`: with
 * no session left the host lands it on the home page and drops the query, so
 * no popup opens.
 */
export const goToLogin = () => {
  window.location.assign(pages.parent.needLogin);
};

export const scrollToTop = () => {
  window.scrollTo({ top: 0, behavior: 'smooth' });
};

const formatter = new Intl.NumberFormat('en-US', {
  maximumFractionDigits: 0,
});

export const formatNumber = (value: number): string => formatter.format(value);

const PRICE_DECIMALS = 2;

/**
 * Formats a price the way the host does (`CurrencyExchange::formatPrice`): the
 * currency sign, then two decimals — or none for a whole number when
 * `noDecimalsIfInteger` is set.
 */
export const formatPrice = (
  value: number,
  currencySign: string,
  noDecimalsIfInteger = false
): string => {
  const amount = Math.abs(value);
  const formatted =
    noDecimalsIfInteger && Number.isInteger(amount)
      ? String(amount)
      : amount.toFixed(PRICE_DECIMALS);

  return `${value < 0 ? '-' : ''}${currencySign}${formatted}`;
};

export const getLocale = (): Locale => window.intlLocale ?? Locale.ENGLISH;

export const getTranslationText = (key: string) =>
  window.intlMessages?.[key] || 'Missing translation';
